import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  confirmSignIn as amplifyConfirmSignIn,
  resetPassword,
  confirmResetPassword,
  updateUserAttribute,
  updateUserAttributes,
  resendSignUpCode,
  updatePassword,
  confirmUserAttribute,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
} from '@aws-amplify/auth';
import { AuthErrorSource } from '../error/auth_middleware';
import { v4 as uuid } from 'uuid';

import {
    SignInSignUpState,
    AuthType,
    RecoveryState,
} from '../../../utils/constants';
import { ErrorListings } from '../error/error_map';

import { getTokenPayload } from '../../api/tokenUtil';
import { decodeToken } from 'react-jwt';

import { getFeatures } from '../../../conf/feature';
import {
    getEnvSettings,
    logInfo,
    isFreeTrial,
    isFreePlan,
} from '../../../utils';

import {
    handleEmailOTPChallenge,
    handleSMSMFAChallenge,
    handleEmailOTPResend,
    handleSignInPassword,
} from './amplify_actions_handlers';

/**
 * Helper function to process authenticated user after successful sign-in
 * Fetches session, extracts token, and processes the user
 * @param {boolean} setupForPlan - Whether this is for plan setup
 * @param {boolean} shouldSignIn - Whether to call env.signIn() (default: true)
 * @returns {Promise<void>}
 */
export async function processAuthenticatedUser ({ setupForPlan = false, shouldSetEnvInfo = true }) {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!session.tokens?.idToken) {
        throw new Error('No ID token found in session');
    }

    const idToken = session.tokens.idToken;
    const tokenString = idToken.toString();
    this.env.userCognitoToken = tokenString;

    if (shouldSetEnvInfo) {
        this.env.signIn();
    }

    // Format the token data to match what processAuthUser expects
    // processAuthUser expects: { idToken: { jwtToken: string, payload: object } }
    let tokenPayload = idToken.payload;
    if (!tokenPayload || Object.keys(tokenPayload).length === 0) {
        // If payload is not available, decode the JWT token
        try {
            tokenPayload = decodeToken(tokenString) || {};
        } catch (decodeErr) {
            logInfo('Failed to decode token, using empty payload: ', decodeErr);
            tokenPayload = {};
        }
    }

    const authUserData = {
        idToken: {
            jwtToken: tokenString,
            payload: tokenPayload,
        },
    };

    this.processAuthUser(authUserData, setupForPlan);
}

export async function login ({ username, password, setupForPlan = false }) {
    this.env.errorStore.clearAuthError();
    this.loginAttempt = true;
    this.isLoading = true;
    this.env.clearStoredInfo();
    this.env.signInSignUpState = { status: SignInSignUpState.VERIFYING_CREDENTIALS };
    try {
        this.loginInfo = {
            username,
            password,
            setupForPlan, // Store setupForPlan in loginInfo
        };
        const output = await amplifySignIn({ username, password });
        logInfo('Login Response: ', output);

        if (output.isSignedIn) {
            // User is signed in, get session and process
            await this.processAuthenticatedUser({ setupForPlan, shouldSetEnvInfo: true });
            this.isLoading = false;
            return;
        }

        // Handle sign-in challenges
        if (handleSignInPassword.call(this, output, username)) {
            this.isLoading = false;
            return;
        }

        this.isLoading = false;
        handleUnexpectedResponse();
    } catch (error) {
        /* Handle Signin Error */
        logInfo('User.amplifyActions.error signing in', error);
        this.isLoading = false;
        this.env.errorStore.handleAuthError({
            error: error,
            source: AuthErrorSource.COGNITO,
            action: 'logIn',
        });

        this.env.errorStore.errors = ErrorListings.loginFailed(error);
        this.env.errorStore.observed.errors.login = uuid();
    }
}

export async function resendForOTP () {
    this.env.errorStore.clearAuthError();
    this.env.signInSignUpState = {
        status: SignInSignUpState.OTP_RESENDING,
        authType: AuthType.CONFIRM_SIGN_IN,
    };

    try {
        // Re-sign in to get a new code - this will trigger a new email OTP
        const output = await amplifySignIn({
            username: this.loginInfo.username,
            password: this.loginInfo.password,
        });

        if (output.isSignedIn) {
            await this.processAuthenticatedUser({ shouldSetEnvInfo: false });
            return;
        }

        // Check if we got the EMAIL_OTP challenge again
        if (handleEmailOTPResend.call(this, output)) {
            return;
        }

        handleUnexpectedResponse();
    } catch (err) {
        logInfo('resendForOTP error: ', err);
        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'resendForOTP',
        });
    }
}

export async function confirmSignIn ({ code }) {
    this.env.errorStore.clearAuthError();
    this.env.signInSignUpState = {
        status: SignInSignUpState.OTP_CONFIRMING,
        authType: AuthType.CONFIRM_SIGN_IN,
    };

    try {
        const output = await amplifyConfirmSignIn({ challengeResponse: code });

        // Check if sign-in is now complete
        if (output.isSignedIn) {
            // Retrieve setupForPlan from loginInfo if it exists
            const setupForPlan = this.loginInfo?.setupForPlan || false;
            await this.processAuthenticatedUser({ setupForPlan, shouldSetEnvInfo: true });
            this.env.signInSignUpState = {
                status: SignInSignUpState.OTP_CONFIRM_SUCCESS,
            };
            return;
        }

        handleUnexpectedResponse();
    } catch (err) {
        console.log('confirmSignIn error: ', err);
        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'confirmSignIn',
        });
        // Don't show popup for OTP errors - they display inline in VerifyOTP component
        // this.env.errorStore.errors = ErrorListings.loginFailed(err);
        // this.env.errorStore.observed.errors.login = uuid();
    }
}

export async function changePassword ({ oldPassword, newPassword }) {
    try {
        await updatePassword({ oldPassword, newPassword });
        this.observed.changePasswordOk = uuid();
    } catch (err) {
        // 3. Handle errors here
        this.env.errorStore.errors = ErrorListings.incorrectPasswordInfo();
        this.env.errorStore.observed.errors.changePassword = uuid();
    }
}

export async function signUp (params) {
    const {
        email,
        firstName,
        lastName,
        password,

        publisherName,
        industry,
        objective,
        phoneNumber,
        timezone,
    } = params;

    this.env.storeUserInfo({
        signUp: {
            email: email,
            password: password,
        },
    });

    amplifySignUp({
        username: email,
        password,
        options: {
            userAttributes: {
                email,
                family_name: lastName,
                given_name: firstName,
                zoneinfo: timezone,
                phone_number: phoneNumber,
                'custom:industry': industry,
                'custom:objective': objective,
                'custom:company': publisherName,
                'custom:configured': '1', /* pass this to cognito because it is configured */
            },
        },
    })
    .then((data) => {
        this.env.signIn();
        logInfo('SignUp Complete Email Verification Sent...', data);
        this.env.signInSignUpState = {
            status: SignInSignUpState.SIGN_UP_CONFIRMATION_PHASE,
        };
        this.env.history.push('/verifyAccount');
    })
    .catch((err) => {
        logInfo('UserStore.signUperror', err);
        const { hasError } = this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'signUp',
        });

        if (!hasError) {
            // error not handled
            this.env.errorStore.errors = ErrorListings.genericAuthError(err);
            this.env.errorStore.observed.errors.authError = uuid();
        }
    });
}

export async function confirmSignUp ({ code }) {
    const storeInfo = this.env.getStoredInfo();
    let email = storeInfo?.signUp?.email || this.env.settings?.tkPayload?.email;
    if (!email) {
        email = storeInfo.recovery?.email;
    }
    const password = storeInfo?.signUp?.password;
    logInfo('Confirming Account: ', email);
    logInfo('Verify Info: ', code);
    amplifyConfirmSignUp({ username: email, confirmationCode: code })
    .then((data) => {
        // console.log('AmplifyActions.confirmSignUp() Confirm Signup Success: ', data);
        this.env.clearStoredInfo();
        const features = getFeatures();
        const envSettings = getEnvSettings();
        console.log('envSettings: ', envSettings);
        let setupForPlan = false;
        if (features?.billing_v4?.active &&
            envSettings &&
            envSettings?.plan
            ) {
            const plan = envSettings.plan;
            if (!isFreeTrial(plan) && !isFreePlan(plan)) {
                setupForPlan = true;
            }
        }

        // instead of auto signin redirect to login
        if (password) {
            // Reuse login function which handles EMAIL_OTP challenge and shows dialog
            this.login({ username: email, password: password, setupForPlan });
        } else {
            this.env.errorStore.errors = ErrorListings.userAlreadyConfirmed();
            this.env.errorStore.observed.errors.confirmSignup = uuid();
        }
    }).catch((err) => {
        logInfo('');
        logInfo('');
        logInfo('Confirmation Error: ', err);
        if (err.message === ErrorListings.message.USER_ALREADY_CONFIRMED) {
            logInfo('will render error for user already confirmed');
            this.env.errorStore.errors = ErrorListings.userAlreadyConfirmed();
            this.env.errorStore.observed.errors.confirmSignup = uuid();
            return;
        }

        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'signUp',
        });
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
    });
}

export async function federatedSignIn (params) {
    // In Amplify v6, params are not used in v6 and federated sign-in uses signInWithRedirect

    try {
        // This will redirect the user to the OAuth provider
        await signInWithRedirect({ provider: 'Google' });
        // Note: After redirect, the user will be handled by the Hub listener in useOpenSignin.js
    } catch (err) {
        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'federatedSignIn',
        });
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
    }
}

export async function signOut () {
    this.env.signOut();
    function navigateOut () {
        const url = window.location.href;
        if (url.indexOf('login') === -1 && url.indexOf('signup') === -1) {
             window.location = '/login';
        }
    }

    try {
        // In Amplify v6, signOut can be called even if user is not authenticated
        // It will clear any local state and tokens
        await amplifySignOut();
        this.env.clearStoredInfo();
        navigateOut();
    } catch (err) {
        logInfo('AWS Signout Error ', err);
        // Even if signOut fails, we should still navigate out to clear local state
        navigateOut();
    }
}

/**
 * Resets the authentication flow state
 * Clears login info and signs out from Amplify to invalidate any active challenges
 */
export async function resetAuthFlow () {
    logInfo('Resetting auth flow state');
    
    // Clear login info (username, password, etc.)
    this.loginInfo = {
        username: '',
        password: '',
    };
    this.isLoading = false;
    this.cognitoUsername = '';
    
    // Reset sign-in state
    this.env.signInSignUpState = {
        status: SignInSignUpState.SIGNED_OUT,
    };
    
    try {
        // Sign out to clear any active Cognito sessions/challenges
        await amplifySignOut();
        this.env.clearStoredInfo();
    } catch (err) {
        logInfo('Error during resetAuthFlow signOut: ', err);
    }
}

export async function completeUserSetup () {
    let hasError = false;
    const user = await getCurrentUser()
    .catch(err => {
        logInfo('completeUserSetup: ', err);
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
        hasError = true;
    });

    if (hasError) {
        return;
    }

    await updateUserAttribute({
        userAttribute: {
          attributeKey: 'custom:configured',
          value: '1',
        },
    })
    .catch(err => {
        logInfo(' completeUserSetup  update user attr: ', err);
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
        hasError = true;
    });

    if (hasError) {
        return;
    }

    // In Amplify v6, res is not 'SUCCESS' anymore, and it returns an empty object.
    // if (res === 'SUCCESS') {
        this.configured = true;
        logInfo('completeUserSetup Success: ', user);
        this.env.signInSignUpState = {
            status: SignInSignUpState.FEDERATED_SIGNIN_SETUP_COMPLETE,
        };

        // In Amplify v6, get token from session instead of user object
        try {
            const session = await fetchAuthSession();
            if (session.tokens?.idToken) {
                this.env.userCognitoToken = session.tokens.idToken.toString();
            }
        } catch (err) {
            logInfo('completeUserSetup - Error fetching session: ', err);
        }

        logInfo('user.amplifyAction().completeUserSetup() -> invoking authGet-PublisherInfo()', this.env.userCognitoToken);
        this.env.signIn();
        if (this.env.userCognitoToken) {
            this.authGetPublisherInfo({ cognitoToken: this.env.userCognitoToken });
        }
    // }
}

export async function forgotPassword ({ email }) {
    this.env.errorStore.clearAuthError();
    this.env.recoveryState = {
      status: RecoveryState.RECOVERY_CODE_SENDING,
    };
    const storeInfo = this.env.getStoredInfo();
    storeInfo.recovery = {
        email,
    };
    this.env.storeUserInfo(storeInfo);

    try {
        // In Amplify v6, resetPassword returns a ResetPasswordOutput object
        const output = await resetPassword({ username: email });
        logInfo('resetPassword output: ', output);

        // Check if the reset was initiated successfully
        if (output.nextStep) {
            logInfo('a verification code is sent, nextStep: ', output.nextStep);
            this.env.recoveryState = {
                status: RecoveryState.RECOVERY_CODE_SENT,
            };
        }
    } catch (err) {
        logInfo('failed with error', err);
        this.env.recoveryState = {
          status: RecoveryState.RECOVERY_CODE_SENT,
        };
        // Handle attempt limit exceeded
        if (err.message === ErrorListings.message.CHANGE_PASSWORD_ATTEMPT_EXCEEDED) {
            logInfo('will render error for attempt limit exceeded');
            this.env.errorStore.errors = ErrorListings.changePassLimitExceeded();
            this.env.errorStore.observed.errors.forgotPassword = uuid();
            throw err;
        }

        if (err.message === ErrorListings.message.UNVERIFIED_ACCOUNT) {
            logInfo('will render error for unverified account');
            this.env.errorStore.errors = ErrorListings.unverifiedAccount();
            this.env.errorStore.observed.errors.forgotPassword = uuid();
            throw err;
        }
        this.env.errorStore.errors = ErrorListings.forgotPasswordUserNotFound(err);
        this.env.errorStore.observed.errors.forgotPassword = uuid();
        throw err;
    };
}

export async function recoverPassword ({ code, password }) {
    this.env.errorStore.clearAuthError();
    this.env.recoveryState = {
        status: RecoveryState.VERIFY_RECOVERY_CODE,
    };
    const storeInfo = this.env.getStoredInfo();
    const email = storeInfo.recovery.email;
    logInfo('Recovering...', email);
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password })
    .then(data => {
        // this.login({ username: email, password });
        this.env.recoveryState = {
          status: RecoveryState.VERIFY_RECOVERY_CODE_COMPLETE,
        };
        this.observed.recoverPasswordSuccess = uuid();
    })
    .catch(err => {
        logInfo(' Password Recovery Errr :', err);

        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'recoverPassword',
        });
        this.env.recoveryState = {
          status: RecoveryState.VERIFY_RECOVERY_CODE_COMPLETE,
        };
        throw err;
    });
}

function forVerification (env) {
    if (env.settings.forVerification) {
        if (!env.settings.tk) {
            return false;
        }
        const payload = getTokenPayload(env.settings.tk);
        if (!payload) {
            return false;
        }
        const p = payload.email.split('::');
        env.settings.tkPayload = {
            ...payload,
            email: p[0],
            pass: p[1],
        };
        return true;
    }
    return false;
}

export async function currentUser () {
    if (this.verifySessionExpired()) return;
    this.env.signIn();
    let session;
    try {
       const authSession = await fetchAuthSession({ forceRefresh: true });
       session = {
           idToken: authSession.tokens?.idToken,
           accessToken: authSession.tokens?.accessToken,
       };
    } catch (err) {
        if (forVerification(this.env)) {
            console.log('Verification Settings:', this.env.settings);
            // return because user may need to verify...
            return;
        }
        // when this happen throw the token error...
        // this.env.history.push('/login');
        logInfo('currentUser() will dispatch SessionError from current User because auth errors');
        this.env.errorStore.dispatchSessionError();
    }

    let hasError = false;

    if (session && session.idToken) {
        const authUser = await getCurrentUser()
        .catch(err => {
            logInfo('currentUser() will dispatch SessionError from current User because of err', err);
            this.env.errorStore.dispatchSessionError();
            /*
            this.env.errorStore.errors = ErrorListings.genericAuthError(err);
            this.env.errorStore.observed.errors.authError = uuid();
            */
            hasError = true;
            console.log('Has Error()....');
        });
        logInfo('amplifyActions.currentUser() AuthUser -> ', authUser);
        if (hasError) {
            return;
        }

        const res = this.manageCognitoPayload(session.idToken.payload);
        if (res && res.hasError) {
            return;
        }

        // this.configured = false;
        if (this.configured) {
            this.env.signInSignUpState = {
                status: SignInSignUpState.IN_SESSION,
            };
        } else {
            this.env.signInSignUpState = {
                status: SignInSignUpState.SETUP_ACCOUNT,
            };
        }

        this.env.showProgress();
        const tokenString = session.idToken.toString();
        this.env.userCognitoToken = tokenString;
        await this.authGetPublisherInfo({ cognitoToken: tokenString });
    }
}

export async function resendSignupCode () {
    const storeInfo = this.env.getStoredInfo();
    let email = storeInfo?.signUp?.email || this.env.settings?.tkPayload?.email;

    if (!email) {
        email = storeInfo.recovery?.email;
    }
    try {
        await resendSignUpCode({ username: email });
        logInfo('code resent successfully');
        this.env.errorStore.errors = ErrorListings.resendCode(null);
        this.env.errorStore.observed.errors.resendSignupCode = uuid();
    } catch (err) {
        console.log('error resending code: ', err);

        this.env.errorStore.errors = ErrorListings.resendCode(err);
        this.env.errorStore.observed.errors.resendSignupCode = uuid();
    }
}

export async function updateUserAttrForProfile ({ userInfo }) {
    /*
       firstName: payload.given_name,
        lastName: payload.family_name,
    */
    let hasError = false;
    await getCurrentUser()
    .catch(err => {
        logInfo('completeUserSetup: ', err);
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
        hasError = true;
    });

    if (hasError) {
        return;
    }

    await updateUserAttributes({
        userAttributes: {
            given_name: userInfo.firstName,
            family_name: userInfo.lastName,
        },
    }).catch(err => {
        this.env.errorStore.handleAuthError({
            error: err,
            source: AuthErrorSource.COGNITO,
            action: 'updateUserAttrForProfile',
        });
        this.env.errorStore.errors = ErrorListings.genericAuthError(err);
        this.env.errorStore.observed.errors.authError = uuid();
        hasError = true;
    });

    if (hasError) {
        return;
    }

    const updatedUser = {
        ...this.user,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
    };

    this.user = updatedUser;
    this.env.storeUserInfo({
        user: this.user,
    });
    this.observed.user = uuid();
}

export async function requiredChangePasssword ({ newPassword }) {
    try {
        logInfo('AmplifyAction.requiredChangePassword() Attempting to confirm new password');

        // Confirm the sign-in with the new password (responds to NEW_PASSWORD_REQUIRED challenge)
        const output = await amplifyConfirmSignIn({
            challengeResponse: newPassword,
        });

        logInfo('AmplifyAction.requiredChangePassword() confirmSignIn output: ', output);

        // Update loginInfo with the new password since the old password is no longer valid
        // This is needed for resendForOTP
        if (this.loginInfo) {
            this.loginInfo.password = newPassword;
        }

        // Check if user is signed in (no MFA required)
        if (output.isSignedIn) {
            logInfo('Password change successful, user signed in');
            this.observed.changePasswordOk = uuid();
            await this.processAuthenticatedUser({ shouldSetEnvInfo: false });
            return;
        }

        // Check for MFA challenges after password change
        if (output.nextStep) {
            const { signInStep } = output.nextStep;

            // Try EMAIL_OTP and SMS_MFA handlers
            if (handleEmailOTPChallenge.call(this, signInStep)) {
                return;
            }

            if (handleSMSMFAChallenge.call(this, signInStep)) {
                return;
            }
        }

        handleUnexpectedResponse();
    } catch (err) {
        logInfo('AmplifyAction.requiredChangePassword() error -> ', err);
        this.env.errorStore.errors = ErrorListings.incorrectPasswordInfo();
        this.env.errorStore.observed.errors.changePassword = uuid();
    }
}

export async function createCognitoSession () {
    let session;
    try {
        const authSession = await fetchAuthSession();
        session = {
          idToken: authSession.tokens?.idToken,
          accessToken: authSession.tokens?.accessToken,
        };
     } catch (err) {
        logInfo('AmplifyAction.createCognitoSession() error -> ', err);
        return;
     }

     if (session && session.idToken) {
         const authUser = await getCurrentUser();
         logInfo('AmplifyAction.createCognitoSession() Auth error -> ', authUser);
    }
}

function handleUnexpectedResponse ({ message = 'Unexpected response' }) {
    this.env.errorStore.errors = ErrorListings.genericAuthError({ message: message });
    this.env.errorStore.observed.errors.authError = uuid();
}

export async function getUserDataFromCognito() {
  try {
    const attrs = await fetchUserAttributes();
    this.userEmail = attrs.email;
    if (!this.userEmail) {
      throw new Error('Email not found for user');
    }
    this.user.userAttributes = attrs;
    this.observed.userAttributes = uuid();
  } catch (err) {
    this.env.errorStore.handleAuthError({
      error: err,
      source: AuthErrorSource.COGNITO,
      action: 'getUserDataFromCognito',
    });
    this.env.errorStore.errors = ErrorListings.genericAuthError(err);
    this.env.errorStore.observed.errors.getUserDataFromCognito = uuid();
  }
}

export async function sendPhoneNumberVerificationCode({ isResend = false } = {}) {
  this.env.errorStore.clearAuthError();
  if (isResend) {
    this.env.signInSignUpState = {
      status: SignInSignUpState.OTP_SENDING,
    };
  }
  try {
    await sendUserAttributeVerificationCode({
      userAttributeKey: 'phone_number',
    });
    logInfo('Phone number verification code sent successfully.');
    if (isResend) {
      this.env.signInSignUpState = {
        status: SignInSignUpState.OTP_SENT,
      };
    }
    this.observed.phoneVerificationCodeSent = uuid();
  } catch (err) {
    logInfo('sendPhoneNumberVerificationCode() error -> ', err);
    this.env.errorStore.handleAuthError({
      error: err,
      source: AuthErrorSource.COGNITO,
      action: 'sendPhoneNumberVerificationCode',
    });
    this.env.errorStore.errors = ErrorListings.genericAuthError(err);
    this.env.errorStore.observed.errors.sendPhoneNumberVerificationCode = uuid();
  }
}

export async function confirmPhoneNumberVerification({ code }) {
  this.env.errorStore.clearAuthError();
  this.env.signInSignUpState = {
    status: SignInSignUpState.OTP_VERIFYING,
  };
  try {
    await confirmUserAttribute({
      userAttributeKey: 'phone_number',
      confirmationCode: code,
    });
    logInfo('Phone number verified successfully.');
    this.env.signInSignUpState = {
      status: SignInSignUpState.OTP_VERIFIED,
    };
    this.observed.phoneVerificationSuccess = uuid();
  } catch (err) {
    logInfo('confirmPhoneNumberVerification() error -> ', err);
    this.env.errorStore.handleAuthError({
      error: err,
      source: AuthErrorSource.COGNITO,
      action: 'confirmPhoneNumberVerification',
    });
    this.env.errorStore.errors = ErrorListings.genericAuthError(err);
    this.env.errorStore.observed.errors.confirmPhoneNumberVerification = uuid();
  }
}


export async function updatePhoneNumber({ phoneNumber }) {
  this.env.errorStore.clearAuthError();
  if (!phoneNumber.startsWith('+')) {
    throw new Error('Phone number must be in E.164 format (e.g. +61412345678)');
  }
  try {
    await updateUserAttributes({
      userAttributes: {
        phone_number: phoneNumber,
      },
    });
  } catch (err) {
    logInfo('updatePhoneNumber() error -> ', err);
    this.env.errorStore.handleAuthError({
      error: err,
      source: AuthErrorSource.COGNITO,
      action: 'updatePhoneNumber',
    });
    this.env.errorStore.errors = ErrorListings.genericAuthError(err);
    this.env.errorStore.observed.errors.updatePhoneNumber = uuid();
  }
}